import i18n from '../i18n/config';
import type { ContentBlock } from '../types';

const MISSING_PATTERNS = ['[missing', 'translation missing'];

export function isMissing(value: unknown): value is null | undefined {
  if (value == null) return true;
  if (typeof value === 'string') {
    return MISSING_PATTERNS.some((prefix) => value.toLowerCase().startsWith(prefix));
  }
  return false;
}

export function localeRoot(): Record<string, unknown> {
  return (i18n.translations[i18n.locale] ?? {}) as Record<string, unknown>;
}

export function readObject<T extends Record<string, unknown>>(key: string): T | undefined {
  const value = i18n.t(key, { returnObjects: true, defaultValue: null });
  if (isMissing(value) || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as T;
}

export function orderedSlugs(
  key: 'product_order' | 'blog_order' | 'category_order' | 'page_order',
): string[] {
  const order = localeRoot()[key];
  return Array.isArray(order) ? order.map(String) : [];
}

export function isPublished(publishedAt: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(publishedAt) <= today;
}

const WORDS_PER_MINUTE = 200;

export function readingMinutes(body: ContentBlock[]): number {
  const words = body.reduce((count, block) => {
    const texts = [
      'text' in block && block.text ? block.text : '',
      ...('items' in block && block.items ? block.items : []),
    ];
    return count + texts.reduce((sum, text) => sum + text.split(/\s+/).filter(Boolean).length, 0);
  }, 0);

  return Math.max(Math.ceil(words / WORDS_PER_MINUTE), 1);
}

export function buildContentBlocks(raw: unknown): ContentBlock[] {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((block) => {
      if (!block || typeof block !== 'object') return null;
      const entry = block as Record<string, unknown>;
      const type = String(entry.type ?? '');

      switch (type) {
        case 'heading':
          return {
            type: 'heading' as const,
            text: String(entry.text ?? ''),
            ...(entry.level != null ? { level: Number(entry.level) } : {}),
          };
        case 'paragraph':
          return { type: 'paragraph' as const, text: String(entry.text ?? '') };
        case 'list':
        case 'ordered_list':
          return {
            type: type as 'list' | 'ordered_list',
            items: Array.isArray(entry.items) ? entry.items.map(String) : [],
          };
        case 'callout':
          return {
            type: 'callout' as const,
            text: String(entry.text ?? ''),
            ...(entry.title ? { title: String(entry.title) } : {}),
          };
        case 'quote':
          return { type: 'quote' as const, text: String(entry.text ?? '') };
        default:
          return null;
      }
    })
    .filter((block): block is ContentBlock => block != null);
}
